import { useState, useCallback, useRef, useEffect } from 'react';
import { useNotifyStore } from '../store/notifyStore.js';
import { useDebugStore } from '../store/debugStore.js';
import { PersonIcon, SettingsIcon, ClockIcon, TrashIcon, CheckIcon, VideoTestIcon, MicTestIcon, SpeakerTestIcon, DebugIcon } from '../components/Icons.jsx';

/**
 * Test the current audio output path with a sweep tone.
 * @param {{ notify: (kind, text) => void }} deps
 */
export async function runSpeakerTest({ notify }) {
  try {
    const ac = new (window.AudioContext || window.webkitAudioContext)();
    const source = ac.createOscillator();
    const gain = ac.createGain();
    source.type = 'sine';
    source.frequency.setValueAtTime(440, ac.currentTime);
    source.frequency.exponentialRampToValueAtTime(880, ac.currentTime + 0.25);
    source.frequency.exponentialRampToValueAtTime(440, ac.currentTime + 0.5);
    gain.gain.setValueAtTime(0.35, ac.currentTime);
    gain.gain.linearRampToValueAtTime(0, ac.currentTime + 0.6);
    source.connect(gain).connect(ac.destination);
    source.start();
    source.stop(ac.currentTime + 0.7);
    await new Promise((resolve) => { source.onended = resolve; });
    await ac.close().catch(() => {});
    notify('success', 'Тест наушников завершён');
  } catch (err) {
    notify('error', `Тест наушников не выполнен: ${err.message}`);
  }
}

/** Resolve a saved audio output device and set it as the destination sink. */
export async function applyAudioOutputSink() {
  try {
    const raw = localStorage.getItem('vcall.settings');
    const settings = raw ? JSON.parse(raw) : {};
    if (!settings['audio.output']) return null;

    const ac = new (window.AudioContext || window.webkitAudioContext)();
    const devices = await navigator.mediaDevices.enumerateDevices();
    const target = devices.find((d) => d.label === settings['audio.output'] || d.deviceId === settings['audio.output']);
    if (target && typeof ac.setSinkId === 'function') {
      await ac.setSinkId(target.deviceId);
    }
    await ac.close().catch(() => {});
    return target ? target.deviceId : null;
  } catch {
    return null;
  }
}

/** Live camera preview panel. */
function VideoPreviewPanel({ notify }) {
  const videoRef = useRef(/** @type {HTMLVideoElement|null} */ (null));
  const streamRef = useRef(/** @type {MediaStream|null} */ (null));
  const [active, setActive] = useState(false);
  const [error, setError] = useState('');

  const startPreview = useCallback(async () => {
    setError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setActive(true);
    } catch (err) {
      setError(err.message);
      notify('error', `Не удалось открыть камеру: ${err.message}`);
    }
  }, [notify]);

  const stopPreview = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setActive(false);
  }, []);

  useEffect(() => {
    return () => {
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  return (
    <div className="card p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Превью камеры</h3>
        {active && (
          <button type="button" className="btn-secondary text-xs px-2 py-1 !text-red-500 hover:!border-red-300" onClick={stopPreview}>
            Закрыть
          </button>
        )}
      </div>

      <div className="aspect-video bg-gray-900 rounded-lg overflow-hidden flex items-center justify-center mb-3 relative">
        {active ? (
          <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-contain" />
        ) : (
          <div className="text-gray-400 text-sm text-center">
            <VideoTestIcon size={32} className="mx-auto mb-2 opacity-50" />
            Камера не запущена
          </div>
        )}
      </div>

      {error && <p className="text-xs text-red-500 mb-2">{error}</p>}

      <div className="flex items-center gap-3">
        {!active ? (
          <button type="button" className="btn-primary text-xs px-3 py-2" onClick={startPreview}>
            <VideoTestIcon size={14} /> Запустить превью
          </button>
        ) : (
          <span className="text-xs text-green-600 flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-green-500" /> Камера работает
          </span>
        )}
      </div>
    </div>
  );
}

/** Microphone test: live audio level meter via AnalyserNode. */
function MicrophoneTestPanel({ notify }) {
  const [level, setLevel] = useState(0);
  const [active, setActive] = useState(false);
  const [error, setError] = useState('');
  const analyserRef = useRef(/** @type {AnalyserNode|null} */ (null));
  const streamRef = useRef(/** @type {MediaStream|null} */ (null));
  const rafRef = useRef(/** @type {number|null} */ (null));

  const startTest = useCallback(async () => {
    setError('');
    try {
      streamRef.current?.getTracks().forEach((t) => t.stop());
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const ac = new (window.AudioContext || window.webkitAudioContext)();
      const analyser = ac.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.6;
      stream.getAudioTracks()[0].start();
      ac.createMediaStreamSource(stream).connect(analyser);
      analyserRef.current = analyser;
      setActive(true);

      const buf = new Uint8Array(analyser.frequencyBinCount);
      const tick = () => {
        analyser.getByteFrequencyData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i++) sum += buf[i];
        const db = Math.max(0, 20 * Math.log10(sum / (buf.length * 255)) + 90) / 3;
        setLevel(Math.min(100, Math.round(db)));
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    } catch (err) {
      setError(err.message);
      notify('error', `Не удалось включить микрофон: ${err.message}`);
    }
  }, [notify]);

  const stopTest = useCallback(() => {
    rafRef.current && cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    analyserRef.current = null;
    setActive(false);
    setLevel(0);
  }, []);

  useEffect(() => {
    return () => {
      rafRef.current && cancelAnimationFrame(rafRef.current);
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  return (
    <div className="card p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Тест микрофона</h3>
        {active && (
          <button type="button" className="btn-secondary text-xs px-2 py-1 !text-red-500 hover:!border-red-300" onClick={stopTest}>
            Остановить
          </button>
        )}
      </div>

      {error && <p className="text-xs text-red-500 mb-3">{error}</p>}

      <div className="mb-3">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-xs text-gray-500">Уровень сигнала</span>
          {active && <span className="text-xs font-medium tabular-nums text-gray-600">{level}%</span>}
        </div>
        <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-100 ${
              level < 25 ? 'bg-amber-400' : level < 60 ? 'bg-green-500' : level < 85 ? 'bg-blue-500' : 'bg-red-500'
            }`}
            style={{ width: `${active ? level : 0}%` }}
          />
        </div>
      </div>

      {!active ? (
        <button type="button" className="btn-primary text-xs px-3 py-2" onClick={startTest}>
          <MicTestIcon size={14} /> Включить микрофон
        </button>
      ) : (
        <span className="text-xs text-green-600 flex items-center gap-1">
          <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" /> Микрофон активен
        </span>
      )}
    </div>
  );
}

/** Speaker/headphone test: play a sweep tone through the output device. */
function SpeakerTestPanel({ notify }) {
  const [playing, setPlaying] = useState(false);
  const [error, setError] = useState('');

  const runTest = useCallback(async () => {
    setError('');
    try {
      const Ac = window.AudioContext || window.webkitAudioContext;
      const ac = new Ac();
      await applyAudioOutputSink();
      // Re-open with the sink already resolved so the tone goes to the right device.
      await ac.close().catch(() => {});

      const ctx = new Ac();
      const devices = await navigator.mediaDevices.enumerateDevices();
      const raw = localStorage.getItem('vcall.settings');
      const settings = raw ? JSON.parse(raw) : {};
      if (settings['audio.output'] && typeof ctx.setSinkId === 'function') {
        const target = devices.find((d) => d.label === settings['audio.output'] || d.deviceId === settings['audio.output']);
        if (target) await ctx.setSinkId(target.deviceId);
      }

      const source = ctx.createOscillator();
      const gain = ctx.createGain();
      source.type = 'sine';
      source.frequency.setValueAtTime(440, ctx.currentTime);
      source.frequency.exponentialRampToValueAtTime(880, ctx.currentTime + 0.25);
      source.frequency.exponentialRampToValueAtTime(440, ctx.currentTime + 0.5);
      gain.gain.setValueAtTime(0.35, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.6);
      source.connect(gain).connect(ctx.destination);
      source.start();
      source.stop(ctx.currentTime + 0.7);
      await new Promise((resolve) => { source.onended = resolve; });
      await ctx.close().catch(() => {});

      setPlaying(true);
      setTimeout(() => setPlaying(false), 800);
      notify('success', 'Тест наушников завершён — сигнал проигран');
    } catch (err) {
      setError(err.message);
      notify('error', `Тест наушников не выполнен: ${err.message}`);
    }
  }, [notify]);

  return (
    <div className="card p-4">
      <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Тест наушников</h3>
      <p className="text-xs text-gray-500 mb-3">Проиграет короткий звуковой сигнал — убедитесь, что слышите его из нужных наушников.</p>

      {error && <p className="text-xs text-red-500 mb-3">{error}</p>}

      {playing && (
        <div className="mb-3 text-xs text-green-600 flex items-center gap-1.5">
          <SpeakerTestIcon size={12} /> Сигнал воспроизведён
        </div>
      )}

      <button type="button" className={`btn-primary text-xs px-3 py-2 ${playing ? 'opacity-50' : ''}`} disabled={playing} onClick={runTest}>
        <SpeakerTestIcon size={14} /> {playing ? 'Воспроизведение…' : 'Проиграть сигнал'}
      </button>
    </div>
  );
}

const SETTINGS = [
  { key: 'audio.input', label: 'Микрофон', type: 'select', category: 'audio' },
  { key: 'audio.output', label: 'Наушники', type: 'select', category: 'audio' },
  { key: 'video.device', label: 'Камера', type: 'select', category: 'video' },
  { key: 'video.resolution', label: 'Разрешение', type: 'select', category: 'video', options: ['640x360', '1280x720', '1920x1080'] },
  { key: 'autoJoin', label: 'Автоприсоединение', type: 'checkbox', category: 'general' },
  { key: 'notifications', label: 'Уведомления', type: 'checkbox', category: 'general' },
  { key: 'debugConsole', label: 'Дебаг-консоль', type: 'checkbox', category: 'debug' },
];

const CATEGORIES = [
  { id: 'general', label: 'Общие', icon: SettingsIcon },
  { id: 'audio', label: 'Аудио', icon: ClockIcon },
  { id: 'video', label: 'Видео', icon: PersonIcon },
  { id: 'tests', label: 'Тесты', icon: VideoTestIcon },
  { id: 'debug', label: 'Дебаг', icon: DebugIcon },
];

function loadSettings() {
  try {
    const raw = localStorage.getItem('vcall.settings');
    if (raw) return JSON.parse(raw);
  } catch {
    // Corrupted storage — start fresh.
  }
  return {};
}

function saveSettings(settings) {
  try {
    localStorage.setItem('vcall.settings', JSON.stringify(settings));
  } catch {
    // Storage full or blocked — silently fail.
  }
}

/** @param {'select'|'checkbox'} type */
async function enumerateDevices(type) {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: type === 'audio', video: type === 'video' });
    stream.getTracks().forEach((t) => t.stop());
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices.filter((d) => d.kind === (type === 'audio' ? 'audioinput' : 'videoinput'));
  } catch {
    return [];
  }
}

/**
 * @param {object} props
 * @param {'select'|'checkbox'} props.type
 * @param {string[]} [props.options]
 * @param {string|boolean|undefined} props.value
 * @param {(value: string|boolean) => void} onChange
 */
function SettingInput({ type, options, value, onChange }) {
  const [local, setLocal] = useState(String(value ?? ''));

  useEffect(() => {
    setLocal(String(value ?? ''));
  }, [value]);

  const handleSelect = useCallback((e) => {
    const v = e.target.value;
    setLocal(v);
    onChange(v);
  }, [onChange]);

  const handleCheck = useCallback((e) => {
    const v = e.target.checked;
    setLocal(String(v));
    onChange(v);
  }, [onChange]);

  if (type === 'select') {
    return (
      <select value={local} onChange={handleSelect} className="bg-white border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-[#002882] transition-colors">
        {options?.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }

  return (
    <label className="flex items-center gap-3 cursor-pointer group">
      <span className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${local === 'true' ? 'bg-[#002882] border-[#002882]' : 'border-gray-300 group-hover:border-gray-400'}`}>
        {local === 'true' && <CheckIcon size={12} className="text-white" />}
      </span>
      <input type="checkbox" checked={local === 'true'} onChange={handleCheck} className="sr-only" />
    </label>
  );
}

export default function Settings() {
  const notify = useNotifyStore((s) => s.notify);
  const setDebugEnabled = useDebugStore((s) => s.setEnabled);
  const [settings, setSettings] = useState(loadSettings);
  const [category, setCategory] = useState('general');
  const [devices, setDevices] = useState(/** @type {MediaDeviceInfo[]} */ ([]));
  const [loading, setLoading] = useState(false);

  // Sync debug store state with the persisted settings checkbox.
  useEffect(() => {
    const enabled = settings['debugConsole'] === true;
    setDebugEnabled(enabled);
  }, [settings['debugConsole'], setDebugEnabled]);

  const handleSave = useCallback(() => {
    saveSettings(settings);
    notify('success', 'Настройки сохранены');
  }, [settings, notify]);

  const handleReset = useCallback(() => {
    setSettings({});
    saveSettings({});
    notify('info', 'Настройки сброшены');
  }, [notify]);

  const handleRefreshDevices = useCallback(async () => {
    setLoading(true);
    try {
      const devs = await enumerateDevices(category === 'video' ? 'video' : 'audio');
      setDevices(devs);
      notify('info', `Найдено устройств: ${devs.length}`);
    } catch (err) {
      notify('error', err.message);
    } finally {
      setLoading(false);
    }
  }, [category, notify]);

  const filtered = SETTINGS.filter((s) => s.category === category);

  return (
    <div className="max-w-4xl mx-auto px-4 py-10">
      <div className="flex items-center justify-between mb-8 animate-rise">
        <div>
          <span className="section-label">Настройки</span>
          <h2 className="page-title mt-2 text-gray-900">Конфигурация приложения</h2>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" className="btn-secondary text-xs px-3 py-1.5" onClick={handleRefreshDevices} disabled={loading}>
            {loading ? 'Обновление…' : 'Обнаружить устройства'}
          </button>
          <button type="button" className="btn-primary text-xs px-3 py-1.5" onClick={handleSave}>
            Сохранить
          </button>
        </div>
      </div>

      <div className="flex gap-1 mb-6 animate-rise" style={{ animationDelay: '0.04s' }}>
        {CATEGORIES.map((c) => (
          <button
            key={c.id}
            type="button"
            className={`btn-secondary text-xs px-4 py-2 ${category === c.id ? '!bg-[#002882] !text-white !border-[#002882]' : ''}`}
            onClick={() => setCategory(c.id)}
          >
            <c.icon size={14} />
            {c.label}
          </button>
        ))}
      </div>

      <div className="space-y-2 animate-rise" style={{ animationDelay: '0.08s' }}>
        {category === 'tests' ? (
          <div className="space-y-3">
            <VideoPreviewPanel notify={notify} />
            <MicrophoneTestPanel notify={notify} />
            <SpeakerTestPanel notify={notify} />
          </div>
        ) : filtered.map((s) => (
          <div key={s.key} className="card p-4 flex items-center justify-between gap-4">
            <span className="text-sm font-medium text-gray-900">{s.label}</span>
            <SettingInput
              type={s.type}
              value={settings[s.key]}
              options={s.type === 'select' && s.category !== 'general' ? devices.map((d) => d.label || d.deviceId) : s.options}
              onChange={(v) => setSettings((prev) => ({ ...prev, [s.key]: v }))}
            />
          </div>
        ))}
      </div>

      {devices.length > 0 && (
        <div className="mt-6 card p-4 animate-rise" style={{ animationDelay: '0.12s' }}>
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Обнаруженные устройства</h3>
          <div className="space-y-2">
            {devices.map((d) => (
              <div key={d.deviceId} className="text-xs text-gray-700 flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[#002882] shrink-0" />
                {d.label || `${d.kind} (${d.deviceId.slice(0, 8)}…)`}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-8 text-center animate-rise" style={{ animationDelay: '0.16s' }}>
        <button type="button" className="btn-secondary text-xs px-4 py-2 !text-red-500 hover:!border-red-300" onClick={handleReset}>
          <TrashIcon size={14} /> Сбросить все настройки
        </button>
      </div>
    </div>
  );
}